/*
 * Authors: mariushirjanu
 * Created: 30/10/2015
 * Upgraded: 31/03/2023
 */
import * as _ from 'underscore';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { BILLINGTYPE_CENTRAL } from '@app/shared/services/billing/billing-data.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { OrderEntryWebshopService } from '../../webshop/order-entry-webshop.service';
import { OrderEntryLocationService } from '../order-entry-location.service';
import { OrderEntryInvoiceService } from './order-entry-invoice.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { IInvoiceAddress, IOrderEntryContact, IShippingAddress, LocationDataType, OrderEntryAddressTypeExtra, OrderEntryLocationDataService } from '../order-entry-location-data.service';
import { OrderEntryRequesterService } from '../../requester/order-entry-requester.service';
import { DialogSize, GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { OrderEntryPaymentService } from '../../payment/order-entry-payment.service';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { WIZARD_ORDER } from '@app/shared/app-wizards';
import { OrderEntryService } from '../../order-entry.service';
import { OrderEntryStepId } from '../../order-entry-steps';
import { IBillingType } from '../base-order-entry-location-to.service';

interface IBillingTypeModel {
  type:IBillingType;
}

@Component({
  selector: 'order-location-billing-type',
  template: `
    <div class="billing-type" *ngIf="fields">
      <h2>
        <span translate>ORDERENTRY.INVOICE.BILLINGTYPE</span>
        <faq-popup faqCode="BILLING_TYPE">
          <i class="fa fa-info-circle info-icon"></i>
        </faq-popup>
      </h2>
      <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true" class="address-type-chooser form-horizontal"></formly>
    </div>
  `
})
export class OrderLocationBillingTypeComponent implements OnInit {
  @Input() public locationToModel:OrderEntryInvoiceService;
  @Input() public dataType:LocationDataType;
  @Output() public onBillingTypeChange:EventEmitter<string> = new EventEmitter<string>();

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IBillingTypeModel;
  public billingTypeField:IExtraFieldConfigurationObject;
  private wizard:GaWizard;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private orderEntryLocationService:OrderEntryLocationService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private translationService:TranslationService,
    private orderEntryRequesterService:OrderEntryRequesterService,
    private orderEntryLocationDataService:OrderEntryLocationDataService,
    private messageService:GaMessageService,
    private deferredService:GaDeferredService,
    private orderEntryPaymentService:OrderEntryPaymentService,
    private orderEntryService:OrderEntryService,
    private wizardService:GaWizardService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);
  }

  public ngOnInit():void {
    this.fields = this.createFields();
  }

  private createFields():IExtraFieldConfigurationObject[] {
    let fields:IExtraFieldConfigurationObject[] = [];

    const billingType:IBillingType = { id: this.orderEntryService.billingTypeId };
    this.model = {
      type: _.clone(this.orderEntryLocationService.billingType(billingType?.id))
    };
    if (this.orderEntryWebshopService.shopContext?.billingTypeChangeAllowed) {
      fields = [
        this.billingTypeField = this.formlyService.aRadio('type', 'ORDERENTRY.INVOICE.BILLINGTYPE')
          .withOptions(this.orderEntryLocationService.billingTypes())
          .withFormat(this.formattingService.formatName)
          .withoutWrapper()
          .onChange(this.billingTypeChange)
          .withExtraOptions({
            templateOptions: {
              trackBy: 'id',
              showBusyWhenDisabled: true
            }
          })
          .build()
      ];
    } else if (billingType?.id === BILLINGTYPE_CENTRAL) {
      fields = [
        this.formlyService.aTemplate(this.translationService.instant(this.model.type?.name))
          .build()
      ];
    }

    return fields;
  }

  private billingTypeChange = (billingType:IBillingType):void => {
    // allow to be only changed by the user and not by reactive form
    if (this.form.enabled) {
      this.form.disable();
      const requesterId:number = this.orderEntryRequesterService.contact?.id;
      const selectedShopId:number = this.orderEntryWebshopService.selectedShop.id;
      const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry.id;
      const testMode:boolean = this.orderEntryWebshopService.testMode;
      const bpId:number = this.orderEntryWebshopService.selectedBP.id;
      const billingTypeId:string = billingType?.id || this.model.type.id;
      this.orderEntryLocationDataService.addresses(this.dataType, requesterId, selectedShopId, selectedCountryId, testMode, bpId, billingTypeId)
        .then(this.checkDefaultInvoiceAddress)
        .then(this.showChangeBillingTypeWarning)
        .then((invoiceAddresses:IInvoiceAddress[]) => {
          // do not allow to add default address when billing type changes from the initial billing type of the order
          const allowToAddCurrentAddress:boolean = billingTypeId === this.orderEntryService.order?.billingType;
          this.locationToModel.setAddressTypeOptions(invoiceAddresses, allowToAddCurrentAddress);
          return this.orderEntryLocationDataService.contacts(this.dataType, requesterId, selectedShopId, selectedCountryId, billingTypeId);
        })
        .then((invoiceContacts:IOrderEntryContact[]) => {
          this.orderEntryPaymentService.billingType = billingTypeId;
          this.locationToModel.billingType = billingType;
          this.orderEntryPaymentService.setPaymentTypes(undefined, true);
          this.locationToModel.setContactTypeOptions(invoiceContacts, true, this.orderEntryService.requesterRequired);

          // when current payment option is not available when changing billing type then clear the payment options and redirect to that tab
          const paymentTypeId:string = this.orderEntryPaymentService.paymentType?.id;
          if (paymentTypeId && !_.findWhere(this.orderEntryPaymentService.paymentTypes, { id: paymentTypeId })) {
            this.orderEntryPaymentService.paymentType = undefined;
            // go back to payment tab
            this.wizard.selectStep(OrderEntryStepId.PAYMENT);
          }

          this.onBillingTypeChange.emit(billingTypeId);
        })
        .catch(() => {
          // user cancels dialog to change billing type -> set billing type back to the another option
          const type:IBillingType = _.find(this.orderEntryLocationService.billingTypes(), (bt:IBillingType) => bt.id !== this.model.type.id);
          this.form.patchValue({ type });
          this.orderEntryLocationDataService.clear(); // clear cache to re-fetch the new list of addresses when trying to again change billing type after the first attempt failed (e.g. no default invoice address)
        })
        .finally(() => {
          this.form.enable();
        });
    }
  };

  private checkDefaultInvoiceAddress = (invoiceAddresses:IInvoiceAddress[] | IShippingAddress[]):Promise<IInvoiceAddress[] | IShippingAddress[]> => {
    const deferred:IGADeferred<IInvoiceAddress[] | IShippingAddress[]> = this.deferredService.defer();

    // when changing billing type to CENTRAL -> make sure that there is a default invoice address display, otherwise display a dialog error message and revert billing type to LOCAL
    if (this.model.type.id === BILLINGTYPE_CENTRAL) {
      const defaultInvoiceAddressFound:boolean = _.findIndex(invoiceAddresses, (invoiceAddress:IInvoiceAddress | IShippingAddress) => invoiceAddress.type === OrderEntryAddressTypeExtra.DEFAULT_INVOICE_ADDRESS && invoiceAddress?.location?.id) !== -1;
      if (!defaultInvoiceAddressFound) {
        this.messageService.showErrorDialog('Change billing type', 'ORDERENTRY.INVOICE.CENTRAL_BILLING_NOT_POSSIBLE').onHide
          .subscribe(() => {
            deferred.reject();
          });

        return deferred.promise;
      }
    }

    deferred.resolve(invoiceAddresses);
    return deferred.promise;
  };

  public showChangeBillingTypeWarning = (invoiceAddresses:IInvoiceAddress[] | IShippingAddress[]):Promise<IInvoiceAddress[] | IShippingAddress[]> => {
    const deferred:IGADeferred<IInvoiceAddress[] | IShippingAddress[]> = this.deferredService.defer();

    const billingTypeWarningDialog:IDialog = this.messageService.showDialog({
      title: 'Change billing type',
      dialogClass: 'warning',
      message: `
        Changing the billing type may have impact on some of your order settings:
        <ul>
          <li>A different invoice address might be selected</li>
          <li>Different charges may apply</li>
          <li>Some payment types might not be available</li>
          <li>You may need to re-enter credit card info if applicable</li>
        </ul>
        Are you sure you want to change the billing type?
      `,
      buttons: [
        {
          id: 'OK',
          label: 'Change billing type',
          className: 'btn-danger',
          onClick: () => {
            deferred.resolve(invoiceAddresses);
            billingTypeWarningDialog?.hide();
          }
        },
        {
          id: 'CANCEL',
          label: 'Don\'t change',
          className: 'btn-success',
          onClick: () => {
            deferred.reject();
            billingTypeWarningDialog.hide();
          }
        }
      ],
      size: DialogSize.MD
    });

    return deferred.promise;
  };
}